import { useQuery } from '@tanstack/react-query'
import { Card, Empty, Flex, Tabs } from 'antd'
import {
  Bell,
  ClipboardList,
  IdCard,
  KeyRound,
  LayoutGrid,
  Monitor
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import type { UserRetrieve, UserShort } from 'src/api/main'
import { attachmentsRetrieveOptions, usersRetrieveOptions } from 'src/api/main'
import { Loading, QueryErrorAlert } from 'src/components'
import { UserDetailAccessTab } from 'src/features/users/components'
import { useWorkspaceUser } from 'src/features/users/hooks'
import { deriveUserStatus } from 'src/features/users/utils'
import { useDocumentTitle, useSessionContext } from 'src/hooks'
import {
  useHasWorkspacePermissions,
  useWorkspacePermissionsContext
} from 'src/hooks'
import { paths } from 'src/router/paths'
import { WorkspacePermissionCode } from 'src/utils'

import { AccountHero } from '../components/AccountHero'
import { AccountProfileEditDrawer } from '../components/AccountProfileEditDrawer'
import { AccountProfileOverviewTab } from '../components/AccountProfileOverviewTab'
import { SecurityTab } from '../components/SecurityTab'

import { AccountAuditPanel } from './AccountAuditPage'
import { AccountNotificationsPanel } from './AccountNotificationsPage'
import { AccountSessionsPanel } from './AccountSessionsPage'
import type { AccountTabKey } from './accountTabs'
import { resolveAccountTab } from './accountTabs'

const userShortToUserRetrieve = (u: UserShort): UserRetrieve => ({
  id: u.id,
  first_name: u.first_name,
  last_name: u.last_name,
  email: u.email,
  phone: u.phone ?? null,
  photo: u.photo ?? null,
  last_login: u.last_login ?? null,
  language: u.language,
  archived: u.archived,
  is_active: u.archived === true ? false : true,
  position: u.position ?? null,
  sso_id: u.sso_id ?? null
})

const accountTabPaths: Record<AccountTabKey, string> = {
  profile: paths.account.profile,
  access: paths.account.access,
  sessions: paths.account.sessions,
  audit: paths.account.audit,
  security: paths.account.security,
  notifications: paths.account.notifications
}

export const AccountPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { tab: tabParam } = useParams<{ tab: string }>()
  const { user: session, activeWorkspaceId } = useSessionContext()
  const sessionUser = session?.user
  const [editOpen, setEditOpen] = useState(false)

  const numericId = sessionUser?.id
  const queryEnabled =
    numericId != null && Number.isFinite(numericId) && numericId > 0

  const { data, isLoading, isError, refetch } = useQuery({
    ...usersRetrieveOptions({ path: { id: numericId ?? 0 } }),
    enabled: queryEnabled
  })

  const sessionProfile = useMemo(
    (): UserRetrieve | undefined =>
      sessionUser != null ? userShortToUserRetrieve(sessionUser) : undefined,
    [sessionUser]
  )

  const profile = data ?? sessionProfile
  const accountWorkspaceId = useMemo(() => {
    const fallback = session?.organizations[0]?.id

    return activeWorkspaceId ?? fallback
  }, [activeWorkspaceId, session?.organizations])

  const workspaceUser = useWorkspaceUser({
    workspaceId: accountWorkspaceId ?? 0,
    userId: numericId ?? 0,
    enabled: queryEnabled && accountWorkspaceId != null
  })

  // The Audit tab is this workspace's log filtered to the signed-in user, so it
  // needs the workspace's own `audit.view`. Kept visible until the permission
  // list resolves, so it does not flicker in for someone who does hold it.
  const [mayManageMembers, mayViewAudit, mayViewApps, mayManageAppAccess] =
    useHasWorkspacePermissions([
      WorkspacePermissionCode.USERS_MANAGE,
      WorkspacePermissionCode.AUDIT_VIEW,
      WorkspacePermissionCode.APPLICATIONS_VIEW,
      WorkspacePermissionCode.APPLICATIONS_ACCESS
    ])
  const canManageMembership = accountWorkspaceId != null && mayManageMembers
  const canManageAppAccess = accountWorkspaceId != null && mayManageAppAccess

  const { permissions } = useWorkspacePermissionsContext()
  const isAuditHidden = permissions !== undefined && !mayViewAudit

  const { data: photoData } = useQuery({
    ...attachmentsRetrieveOptions({ path: { id: Number(profile?.photo?.id) } }),
    enabled: !!profile?.photo
  })

  const resolved = resolveAccountTab(tabParam)
  const accountTabLabels: Record<AccountTabKey, string> = {
    profile: t('shell.account.profile'),
    access: t('users.detail.tabs.access'),
    sessions: t('users.detail.tabs.sessions'),
    audit: t('users.detail.tabs.audit'),
    security: t('shell.account.security'),
    notifications: t('shell.account.notifications')
  }
  const activeTab = resolved?.tab

  useDocumentTitle(activeTab ? accountTabLabels[activeTab] : undefined)
  const legacyActivityTarget = useMemo(() => {
    if (tabParam !== 'activity') return null
    const params = new URLSearchParams(location.search)

    return params.get('view') === 'audit'
      ? paths.account.audit
      : paths.account.sessions
  }, [location.search, tabParam])

  if (sessionUser == null) {
    return <Loading />
  }

  if (!resolved) {
    return <Navigate to={paths.account.profile} replace />
  }

  if (legacyActivityTarget) {
    return <Navigate to={legacyActivityTarget} replace />
  }

  if (resolved.redirect) {
    return <Navigate to={resolved.redirect} replace />
  }

  if (isLoading && profile == null) {
    return <Loading />
  }

  if (isError && sessionProfile == null) {
    return (
      <Card variant="outlined">
        <QueryErrorAlert onRetry={() => void refetch()} />
      </Card>
    )
  }

  if (!profile) {
    return (
      <div role="status" aria-live="polite">
        <Empty description={t('not_found.title')} />
      </div>
    )
  }

  const record = workspaceUser.record
  const status = record ? deriveUserStatus(record) : undefined
  const workspaceId = accountWorkspaceId
  const isWorkspaceRecordMissing = !workspaceUser.isLoading && record == null
  // The tab is application access, which the backend serves and writes on
  // `applications.access` — a membership record alone is not enough.
  const isAppAccessHidden = isWorkspaceRecordMissing || !mayManageAppAccess
  const openEditDrawer = () => setEditOpen(true)

  return (
    <Flex vertical gap={14}>
      <AccountHero
        profile={profile}
        record={record}
        status={status}
        photoSrc={photoData?.file}
        onEdit={openEditDrawer}
      />

      <Tabs
        activeKey={activeTab}
        onChange={(key) => navigate(accountTabPaths[key as AccountTabKey])}
        tabBarStyle={{ marginBottom: 0 }}
        items={[
          {
            key: 'profile',
            label: (
              <Flex align="center" gap={6}>
                <IdCard size={14} />
                <span>{t('users.detail.tabs.overview')}</span>
              </Flex>
            )
          },
          ...(isAppAccessHidden
            ? []
            : [
                {
                  key: 'access',
                  label: (
                    <Flex align="center" gap={6}>
                      <LayoutGrid size={14} />
                      <span>{t('users.detail.tabs.access')}</span>
                    </Flex>
                  )
                }
              ]),
          {
            key: 'sessions',
            label: (
              <Flex align="center" gap={6}>
                <Monitor size={14} />
                <span>{t('users.detail.tabs.sessions')}</span>
              </Flex>
            )
          },
          ...(isAuditHidden
            ? []
            : [
                {
                  key: 'audit',
                  label: (
                    <Flex align="center" gap={6}>
                      <ClipboardList size={14} />
                      <span>{t('users.detail.tabs.audit')}</span>
                    </Flex>
                  )
                }
              ]),
          {
            key: 'security',
            label: (
              <Flex align="center" gap={6}>
                <KeyRound size={14} />
                <span>{t('shell.account.security')}</span>
              </Flex>
            )
          },
          {
            key: 'notifications',
            label: (
              <Flex align="center" gap={6}>
                <Bell size={14} />
                <span>{t('shell.account.notifications')}</span>
              </Flex>
            )
          }
        ]}
      />

      {activeTab === 'profile' && (
        <AccountProfileOverviewTab
          profile={profile}
          record={record}
          workspaceId={workspaceId}
          photoSrc={photoData?.file}
          onEdit={openEditDrawer}
          canManageMembership={canManageMembership}
          canViewAudit={mayViewAudit}
          canViewApps={mayViewApps}
        />
      )}
      {activeTab === 'access' && isAppAccessHidden ? (
        <Navigate to={paths.account.profile} replace />
      ) : null}
      {activeTab === 'access' && !isAppAccessHidden && record && workspaceId ? (
        <UserDetailAccessTab
          readOnly={!canManageAppAccess}
          workspaceId={workspaceId}
          userId={profile.id}
          organizationAccessId={record.id}
          currentRoles={record.roles}
        />
      ) : null}
      {activeTab === 'sessions' && <AccountSessionsPanel />}
      {activeTab === 'audit' && isAuditHidden ? (
        <Navigate to={paths.account.profile} replace />
      ) : null}
      {activeTab === 'audit' && !isAuditHidden ? <AccountAuditPanel /> : null}
      {activeTab === 'security' && <SecurityTab />}
      {activeTab === 'notifications' && <AccountNotificationsPanel />}

      <AccountProfileEditDrawer
        open={editOpen}
        profile={profile}
        record={record}
        workspaceId={workspaceId}
        photoSrc={photoData?.file}
        onClose={() => setEditOpen(false)}
      />
    </Flex>
  )
}
